import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaArrowRight,
  FaCheck,
  FaChevronDown,
  FaPeopleArrows,
  FaUsers,
  FaTimes,
} from "react-icons/fa";
import { AuthContext } from "../AuthProvider";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import GenderIcon from "../components/GenderIcon";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import { calculateAge, parseToDate } from "../utils/dateUtils";
import { translateClassName } from "../utils/classroomUtils";
import {
  fetchAvailableClassrooms,
  fetchClassroomChildren,
  fetchClassroomData,
} from "../utils/classroomHelpers";
import { migrateChildrenToClassroom } from "../utils/migrateHelpers";

import BackButton, { useLeaveForm } from "../components/navigation/BackButton";
type EntityId = string | number;
type DateValue = string | number | Date | null | undefined;

type Classroom = {
  id: EntityId;
  className: string;
  startDate?: DateValue;
  endDate?: DateValue;
};

type ClassroomChild = {
  id: EntityId;
  firstName: string;
  lastName: string;
  gender?: string | null;
  dateBirth?: DateValue;
  committed?: boolean | null;
  waiting?: boolean | null;
};

type MigrationResult = {
  childId: EntityId;
  success: boolean;
  reason?: string | null;
};

type PageError = {
  message: string;
  status?: number;
};

type AuthContextValue = {
  keycloak?: {
    token?: string;
  };
};

const getYear = (value: DateValue): number | undefined =>
  parseToDate(value)?.getFullYear();

const idsEqual = (left: EntityId | undefined, right: EntityId | undefined) =>
  String(left ?? "") === String(right ?? "");

const ClassroomMigrate = () => {
  const { keycloak } = useContext(AuthContext) as AuthContextValue;
  const { id } = useParams<{ id: string }>();
  const leaveForm = useLeaveForm();
  const { t } = useTranslation();

  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [children, setChildren] = useState<ClassroomChild[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<EntityId[]>([]);
  const [availableClassrooms, setAvailableClassrooms] = useState<Classroom[]>([]);
  const [targetClassroomId, setTargetClassroomId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [migrationResults, setMigrationResults] = useState<MigrationResult[]>([]);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [error, setError] = useState<PageError | null>(null);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      if (!id || !keycloak?.token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [classroomRes, childrenRes, classroomsRes] = await Promise.all([
          fetchClassroomData(id, keycloak.token),
          fetchClassroomChildren(id, keycloak.token),
          fetchAvailableClassrooms(keycloak.token),
        ]);

        if (!active) return;

        const typedClassroom = classroomRes as Classroom;
        const typedChildren = childrenRes as ClassroomChild[];
        const typedClassrooms = classroomsRes as Classroom[];
        const currentStartYear = getYear(typedClassroom.startDate);
        const nextYear = currentStartYear ? currentStartYear + 1 : null;

        const sorted = typedClassrooms
          .filter((candidate) => {
            const startYear = getYear(candidate.startDate);
            return (
              !idsEqual(candidate.id, id) &&
              nextYear !== null &&
              startYear === nextYear
            );
          })
          .sort((left, right) => {
            const leftStart = parseToDate(left.startDate);
            const rightStart = parseToDate(right.startDate);

            if (left.className === right.className) {
              return (
                (leftStart?.getTime() ?? 0) -
                (rightStart?.getTime() ?? 0)
              );
            }

            return left.className.localeCompare(right.className);
          });

        setClassroom(typedClassroom);
        setChildren(typedChildren);
        setAvailableClassrooms(sorted);
      } catch (caughtError) {
        if (!active) return;

        const responseStatus =
          typeof caughtError === "object" &&
          caughtError !== null &&
          "response" in caughtError &&
          typeof caughtError.response === "object" &&
          caughtError.response !== null &&
          "status" in caughtError.response
            ? Number(caughtError.response.status)
            : undefined;

        setError({
          message: t("classrooms.migrate.load_error", {
            defaultValue: "Failed to load migration data.",
          }),
          status: responseStatus,
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadData();

    return () => {
      active = false;
    };
  }, [id, keycloak?.token, t]);

  const selectedTarget = useMemo(
    () =>
      availableClassrooms.find((candidate) =>
        idsEqual(candidate.id, targetClassroomId),
      ),
    [availableClassrooms, targetClassroomId],
  );

  const allSelected =
    children.length > 0 && selectedChildren.length === children.length;
  const allSkipped =
    migrationResults.length > 0 &&
    migrationResults.every((result) => !result.success);
  const successfulCount = migrationResults.filter(
    (result) => result.success,
  ).length;
  const skippedCount = migrationResults.length - successfulCount;

  const toggleChildSelection = (childId: EntityId) => {
    setSelectedChildren((current) =>
      current.some((selectedId) => idsEqual(selectedId, childId))
        ? current.filter((selectedId) => !idsEqual(selectedId, childId))
        : [...current, childId],
    );
  };

  const toggleAllChildren = () => {
    setSelectedChildren(allSelected ? [] : children.map((child) => child.id));
  };

  const handleMigrate = async () => {
    if (!targetClassroomId || selectedChildren.length === 0 || !keycloak?.token) {
      return;
    }

    setMigrating(true);

    try {
      const results = (await migrateChildrenToClassroom(
        targetClassroomId,
        selectedChildren,
        keycloak.token,
      )) as MigrationResult[];

      setMigrationResults(results);
      setShowModal(false);
      setShowResultsModal(true);
    } catch (caughtError) {
      setShowModal(false);
      setError({
        message:
          caughtError instanceof Error
            ? caughtError.message
            : t("classrooms.migrate.migration_error", {
                defaultValue: "The classroom migration could not be completed.",
              }),
      });
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    if (!showModal && !showResultsModal) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || migrating) {
        return;
      }

      if (showResultsModal) {
        setShowResultsModal(false);
        leaveForm(`/school/classrooms/${targetClassroomId}/details`);
        return;
      }

      setShowModal(false);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    showModal,
    showResultsModal,
    migrating,
    leaveForm,
    targetClassroomId,
  ]);


  if (loading) return <Spinner />;

  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!classroom) {
    return (
      <p>
        {t("classrooms.common.none_available", {
          defaultValue: "No classroom available.",
        })}
      </p>
    );
  }

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-white sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={`/school/classrooms/${id}/details`} />
        </div>

        <div className="flex flex-col gap-6">
          <section className="relative overflow-hidden rounded-[28px] border border-sky-100 bg-gradient-to-br from-white via-sky-50/70 to-blue-100/80 shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-sky-500/20 dark:from-slate-900 dark:via-sky-950/35 dark:to-slate-900">
            <div className="absolute inset-y-0 right-0 hidden w-[42%] overflow-hidden lg:block">
              <div className="absolute -bottom-20 -right-12 h-72 w-72 rounded-full bg-sky-200/45 dark:bg-sky-500/10" />
              <div className="absolute right-14 top-8 h-32 w-32 rounded-full border-[18px] border-sky-200/60 dark:border-sky-500/15" />
              <div className="absolute bottom-7 right-24 flex h-32 w-32 items-center justify-center rounded-[36px] bg-white/35 text-sky-400/45 backdrop-blur-sm dark:bg-white/5 dark:text-sky-300/20">
                <FaPeopleArrows className="h-16 w-16" aria-hidden="true" />
              </div>
            </div>

            <div className="relative z-10 flex min-h-[218px] items-center px-6 py-8 sm:px-9 lg:px-10">
              <div className="flex min-w-0 flex-col gap-6 sm:flex-row sm:items-center">
                <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-100 text-indigo-600 shadow-[0_12px_35px_rgba(99,102,241,0.16)] ring-1 ring-sky-100 dark:border-slate-900 dark:bg-indigo-950/70 dark:text-indigo-300 dark:ring-sky-500/20">
                  <FaPeopleArrows className="h-11 w-11" aria-hidden="true" />
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
                    {t("classrooms.migrate.banner_eyebrow", {
                      defaultValue: "Classroom migration",
                    })}
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                    {t("classrooms.migrate.banner_title", {
                      defaultValue: "Migrate {{className}}",
                      className: classroom.className,
                    })}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                    {t("classrooms.migrate.banner_description", {
                      defaultValue:
                        "Move selected students from the current classroom to a classroom in the next school year.",
                    })}
                  </p>

                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-sm font-semibold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-300">
                      {getYear(classroom.startDate) ?? "?"} – {getYear(classroom.endDate) ?? "?"}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/85 px-3.5 py-1.5 text-sm font-semibold text-slate-700 shadow-sm dark:border-indigo-500/30 dark:bg-white/10 dark:text-slate-100">
                      <FaUsers className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-300" aria-hidden="true" />
                      {t("classrooms.migrate.selected_count", {
                        defaultValue: "{{selected}} of {{total}} selected",
                        selected: selectedChildren.length,
                        total: children.length,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

        <section className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-5">
          <div className="flex flex-col gap-4 rounded-[18px] border border-sky-200/80 bg-sky-50/60 p-5 shadow-sm dark:border-sky-500/40 dark:bg-sky-950/25 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                {t("classrooms.migrate.select_children", {
                  defaultValue: "Select children",
                })}
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("classrooms.migrate.select_children_description", {
                  defaultValue:
                    "Choose which children should move to the next school year.",
                })}
              </p>
            </div>

            {children.length > 0 && (
              <button
                type="button"
                onClick={toggleAllChildren}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
              >
                {allSelected
                  ? t("classrooms.migrate.deselect_all", {
                      defaultValue: "Deselect all",
                    })
                  : t("classrooms.migrate.select_all", {
                      defaultValue: "Select all",
                    })}
              </button>
            )}
          </div>

          {children.length > 0 ? (
            <LdsTableCard className="mt-5 overflow-hidden">
              <LdsTableScroll>
                <LdsTable>
                  <LdsTableHead>
                    <tr>
                      <LdsTableHeadCell className="w-20 text-center">
                        {t("classrooms.migrate.table.select", {
                          defaultValue: "Select",
                        })}
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        {t("children.table.child_name", {
                          defaultValue: "Child",
                        })}
                      </LdsTableHeadCell>
                      <LdsTableHeadCell className="w-28 text-center">
                        {t("children.table.child_age", {
                          defaultValue: "Age",
                        })}
                      </LdsTableHeadCell>
                    </tr>
                  </LdsTableHead>

                  <LdsTableBody>
                    {children.map((child) => {
                      const selected = selectedChildren.some((selectedId) =>
                        idsEqual(selectedId, child.id),
                      );

                      return (
                        <LdsTableRow
                          key={child.id}
                          onClick={() => toggleChildSelection(child.id)}
                          aria-selected={selected}
                          className={`cursor-pointer ${
                            selected
                              ? "!bg-indigo-50/70 dark:!bg-indigo-500/10"
                              : ""
                          }`}
                        >
                          <LdsTableCell className="text-center">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleChildSelection(child.id)}
                              onClick={(event) => event.stopPropagation()}
                              aria-label={t(
                                "classrooms.migrate.select_child",
                                {
                                  defaultValue: "Select {{name}}",
                                  name: `${child.firstName} ${child.lastName}`,
                                },
                              )}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
                            />
                          </LdsTableCell>

                          <LdsTableCell>
                            <div className="flex flex-wrap items-center gap-2.5">
                              <GenderIcon gender={child.gender} />
                              <span className="font-semibold text-slate-900 dark:text-white">
                                {child.firstName} {child.lastName}
                              </span>
                              <CommittedValidate committed={child.committed} />
                              <WaitingValidate waiting={child.waiting} />
                            </div>
                          </LdsTableCell>

                          <LdsTableCell className="text-center font-medium">
                            {calculateAge(child.dateBirth)}
                          </LdsTableCell>
                        </LdsTableRow>
                      );
                    })}
                  </LdsTableBody>
                </LdsTable>
              </LdsTableScroll>
            </LdsTableCard>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <FaUsers className="h-5 w-5" aria-hidden="true" />
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t("classrooms.details.no_children_assigned", {
                  defaultValue: "No children are assigned to this classroom.",
                })}
              </p>
            </div>
          )}
        </section>

        <section className="rounded-[24px] border border-emerald-200/80 bg-emerald-50/55 p-5 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-950/20 sm:p-6">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <label
                htmlFor="targetClassroom"
                className="block text-sm font-bold text-slate-900 dark:text-white"
              >
                {t("classrooms.migrate.select_target", {
                  defaultValue: "Target classroom",
                })}
              </label>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("classrooms.migrate.select_target_description", {
                  defaultValue:
                    "Only classrooms from the next school year are shown.",
                })}
              </p>
              <div className="relative mt-3">
                <select
                  id="targetClassroom"
                  value={targetClassroomId}
                  onChange={(event) => setTargetClassroomId(event.target.value)}
                  className="min-h-12 w-full appearance-none rounded-xl border border-slate-300 bg-white px-4 py-3 pr-11 text-sm font-semibold text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-400/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:focus:border-slate-400"
                >
                  <option value="">
                    {t("classrooms.migrate.select_placeholder", {
                      defaultValue: "Select a classroom",
                    })}
                  </option>
                  {availableClassrooms.map((candidate) => (
                    <option key={candidate.id} value={String(candidate.id)}>
                      {candidate.className} ({getYear(candidate.startDate) ?? "?"} – {getYear(candidate.endDate) ?? "?"})
                    </option>
                  ))}
                </select>
                <FaChevronDown
                  className="pointer-events-none absolute right-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500"
                  aria-hidden="true"
                />
              </div>

              {availableClassrooms.length === 0 && (
                <p className="mt-3 text-sm font-medium text-amber-700 dark:text-amber-300">
                  {t("classrooms.migrate.no_target_classrooms", {
                    defaultValue:
                      "No classrooms are available for the next school year.",
                  })}
                </p>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row lg:justify-end">
              <BackButton fallback={`/school/classrooms/${id}/details`} />

              <button
                type="button"
                onClick={() => setShowModal(true)}
                disabled={
                  selectedChildren.length === 0 ||
                  !targetClassroomId ||
                  migrating
                }
                className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-45 motion-reduce:hover:translate-y-0 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                {t("classrooms.migrate.migrate_button", {
                  defaultValue: "Migrate children",
                })}
                <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
      </div>

      {showModal &&
        selectedTarget &&
        createPortal(
        <div
          className="fixed inset-0 z-[9999] flex min-h-dvh w-screen items-center justify-center overflow-y-auto bg-slate-950/35 px-4 py-8 backdrop-blur-[2px] dark:bg-black/55"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !migrating) {
              setShowModal(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="classroom-migration-dialog-title"
            className="my-auto w-full max-w-3xl overflow-hidden rounded-[30px] border border-white/80 bg-white shadow-[0_40px_120px_rgba(15,23,42,0.38)] dark:border-white/10 dark:bg-slate-950"
          >
            <div className="relative px-6 pb-7 pt-8 text-center sm:px-10 sm:pb-9 sm:pt-10">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={migrating}
                aria-label={t("common.close", { defaultValue: "Close" })}
                className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:text-white"
              >
                <FaTimes className="h-4 w-4" aria-hidden="true" />
              </button>

              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-indigo-50 text-indigo-600 shadow-[0_16px_40px_rgba(79,70,229,0.18)] ring-1 ring-indigo-100 dark:border-slate-950 dark:bg-indigo-950/70 dark:text-indigo-300 dark:ring-indigo-500/30">
                <FaPeopleArrows className="h-8 w-8" aria-hidden="true" />
              </div>

              <p className="mt-6 text-xs font-bold uppercase tracking-[0.24em] text-indigo-600 dark:text-indigo-300">
                {t("classrooms.migrate.confirm_eyebrow", {
                  defaultValue: "Confirm migration",
                })}
              </p>
              <h2
                id="classroom-migration-dialog-title"
                className="mt-3 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl"
              >
                {t("classrooms.migrate.confirm_heading", {
                  defaultValue: "Move students to the next school year",
                })}
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                {t("classrooms.migrate.confirm_description", {
                  defaultValue:
                    "You are about to move the selected students to another classroom. Review the migration details before continuing.",
                })}
              </p>

              <div className="mt-7 overflow-hidden rounded-[22px] border border-slate-200 bg-slate-50/70 text-left dark:border-white/10 dark:bg-white/5">
                <div className="border-b border-slate-200 px-5 py-4 text-center dark:border-white/10 sm:px-7">
                  <p className="text-sm text-slate-700 dark:text-slate-200 sm:text-base">
                    {t("classrooms.migrate.confirm_summary", {
                      defaultValue:
                        "You are moving {{count}} student(s) from {{source}} to {{target}}.",
                      count: selectedChildren.length,
                      source: classroom.className,
                      target: selectedTarget.className,
                    })}
                  </p>
                </div>

                <div className="grid sm:grid-cols-3">
                  <div className="flex items-center gap-3 px-5 py-5 sm:border-r sm:border-slate-200 dark:sm:border-white/10">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                      <FaUsers className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        {t("classrooms.migrate.from", { defaultValue: "From" })}
                      </p>
                      <p className="mt-1 truncate font-bold text-slate-950 dark:text-white">
                        {translateClassName(classroom)}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {getYear(classroom.startDate) ?? "?"} – {getYear(classroom.endDate) ?? "?"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 border-t border-slate-200 px-5 py-5 dark:border-white/10 sm:border-r sm:border-t-0 dark:sm:border-white/10">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600 dark:bg-sky-950/70 dark:text-sky-300">
                      <FaArrowRight className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        {t("classrooms.migrate.to", { defaultValue: "To" })}
                      </p>
                      <p className="mt-1 truncate font-bold text-slate-950 dark:text-white">
                        {selectedTarget.className}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {getYear(selectedTarget.startDate) ?? "?"} – {getYear(selectedTarget.endDate) ?? "?"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 border-t border-slate-200 px-5 py-5 dark:border-white/10 sm:border-t-0">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300">
                      <FaUsers className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        {t("classrooms.migrate.students", {
                          defaultValue: "Students",
                        })}
                      </p>
                      <p className="mt-1 font-bold text-slate-950 dark:text-white">
                        {selectedChildren.length}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t("classrooms.migrate.selected_label", {
                          defaultValue: "selected",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-5 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:justify-end sm:px-10">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={migrating}
                className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-white/5 dark:text-slate-100 dark:hover:text-white"
              >
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={() => void handleMigrate()}
                disabled={migrating}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-indigo-600 px-7 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(79,70,229,0.25)] transition hover:-translate-y-0.5 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaPeopleArrows className="h-4 w-4" aria-hidden="true" />
                {migrating
                  ? t("classrooms.migrate.migrating", {
                      defaultValue: "Migrating...",
                    })
                  : t("classrooms.migrate.start_migration", {
                      defaultValue: "Start migration",
                    })}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showResultsModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex min-h-dvh w-screen items-center justify-center overflow-y-auto bg-slate-950/35 px-4 py-8 backdrop-blur-[2px] dark:bg-black/55"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setShowResultsModal(false);
                leaveForm(`/school/classrooms/${targetClassroomId}/details`);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="classroom-migration-results-title"
              className="my-auto w-full max-w-3xl overflow-hidden rounded-[30px] border border-white/80 bg-white shadow-[0_40px_120px_rgba(15,23,42,0.38)] dark:border-white/10 dark:bg-slate-950"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="relative px-6 pb-7 pt-8 text-center sm:px-10 sm:pb-9 sm:pt-10">
                <button
                  type="button"
                  onClick={() => {
                    setShowResultsModal(false);
                    leaveForm(`/school/classrooms/${targetClassroomId}/details`);
                  }}
                  aria-label={t("common.close", { defaultValue: "Close" })}
                  className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:text-white"
                >
                  <FaTimes className="h-4 w-4" aria-hidden="true" />
                </button>

                <div
                  className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full border-4 border-white shadow-[0_16px_40px_rgba(15,23,42,0.12)] ring-1 dark:border-slate-950 ${
                    allSkipped
                      ? "bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-950/70 dark:text-amber-300 dark:ring-amber-500/30"
                      : skippedCount > 0
                        ? "bg-sky-50 text-sky-600 ring-sky-100 dark:bg-sky-950/70 dark:text-sky-300 dark:ring-sky-500/30"
                        : "bg-emerald-50 text-emerald-600 ring-emerald-100 dark:bg-emerald-950/70 dark:text-emerald-300 dark:ring-emerald-500/30"
                  }`}
                >
                  {allSkipped ? (
                    <span className="text-3xl font-black" aria-hidden="true">
                      !
                    </span>
                  ) : (
                    <FaCheck className="h-8 w-8" aria-hidden="true" />
                  )}
                </div>

                <p className="mt-6 text-xs font-bold uppercase tracking-[0.24em] text-indigo-600 dark:text-indigo-300">
                  {t("classrooms.migrate.results_eyebrow", {
                    defaultValue: "Migration completed",
                  })}
                </p>
                <h2
                  id="classroom-migration-results-title"
                  className="mt-3 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl"
                >
                  {t("classrooms.migrate.result_title", {
                    defaultValue: "Migration results",
                  })}
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                  {t("classrooms.migrate.results_description", {
                    defaultValue:
                      "Review which students were migrated and whether any students were skipped.",
                  })}
                </p>

                <div className="mt-7 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-[20px] border border-slate-200 bg-slate-50/80 p-4 text-left dark:border-white/10 dark:bg-white/5">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                      {t("classrooms.migrate.total", {
                        defaultValue: "Total",
                      })}
                    </p>
                    <p className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
                      {migrationResults.length}
                    </p>
                  </div>

                  <div className="rounded-[20px] border border-emerald-200 bg-emerald-50/70 p-4 text-left dark:border-emerald-500/30 dark:bg-emerald-950/30">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                      {t("classrooms.migrate.migrated", {
                        defaultValue: "Migrated",
                      })}
                    </p>
                    <p className="mt-2 text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                      {successfulCount}
                    </p>
                  </div>

                  <div className="rounded-[20px] border border-amber-200 bg-amber-50/70 p-4 text-left dark:border-amber-500/30 dark:bg-amber-950/30">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                      {t("classrooms.migrate.skipped", {
                        defaultValue: "Skipped",
                      })}
                    </p>
                    <p className="mt-2 text-2xl font-bold text-amber-800 dark:text-amber-200">
                      {skippedCount}
                    </p>
                  </div>
                </div>

                <div className="mt-5 max-h-[340px] overflow-y-auto rounded-[22px] border border-slate-200 bg-slate-50/60 p-2 text-left dark:border-white/10 dark:bg-white/[0.03]">
                  <ul className="space-y-2">
                    {migrationResults.map((result, index) => {
                      const child = children.find((candidate) =>
                        idsEqual(candidate.id, result.childId),
                      );
                      const name = child
                        ? `${child.firstName} ${child.lastName}`
                        : String(result.childId);

                      return (
                        <li
                          key={`${result.childId}-${index}`}
                          className="flex items-start gap-3 rounded-[16px] border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/5"
                        >
                          <span
                            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                              result.success
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                            }`}
                          >
                            {result.success ? (
                              <FaCheck
                                className="h-3 w-3"
                                aria-hidden="true"
                              />
                            ) : (
                              <span
                                className="text-xs font-black"
                                aria-hidden="true"
                              >
                                !
                              </span>
                            )}
                          </span>

                          <div className="min-w-0">
                            <p className="font-semibold text-slate-950 dark:text-white">
                              {name}
                            </p>
                            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                              {result.success
                                ? t("classrooms.migrate.result_success", {
                                    defaultValue:
                                      "Migrated successfully",
                                  })
                                : t("classrooms.migrate.result_skipped", {
                                    defaultValue: "Skipped: {{reason}}",
                                    reason:
                                      result.reason ??
                                      t(
                                        "classrooms.migrate.unknown_reason",
                                        {
                                          defaultValue: "Unknown reason",
                                        },
                                      ),
                                  })}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end border-t border-slate-200 bg-slate-50/70 px-6 py-5 dark:border-white/10 dark:bg-white/[0.03] sm:px-10">
                <button
                  type="button"
                  onClick={() => {
                    setShowResultsModal(false);
                    leaveForm(`/school/classrooms/${targetClassroomId}/details`);
                  }}
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-indigo-600 px-7 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(79,70,229,0.25)] transition hover:-translate-y-0.5 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                >
                  {t("common.close", { defaultValue: "Close" })}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </SlidePageTransition>
    </PageTemplate>
  );
};

export default ClassroomMigrate;
